/**
 * Helper to validate service worker registration state before triggering update
 */

export interface UpdatableWorkerRegistration {
	active?: {
		state?: string;
	} | null;
}

/**
 * Checks whether a service worker registration is valid and safe to update.
 * Prevents InvalidStateError or "The Service Worker registration has been uninstalled"
 * when calling registration.update().
 */
export function canUpdateServiceWorker<T extends UpdatableWorkerRegistration>(
	registration: T | null | undefined
): registration is T {
	if (!registration?.active) {
		return false;
	}

	if (registration.active.state === 'redundant') {
		return false;
	}

	return true;
}

/**
 * Checks whether a service worker update failure was caused by a transient network/fetch failure
 * (e.g. offline, connection reset, interrupted download, or script fetch block)
 * rather than an unexpected application bug.
 */
export function isServiceWorkerUpdateNetworkError(
	error: unknown,
	isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true
): boolean {
	if (!isOnline) {
		return true;
	}

	if (!error) {
		return false;
	}

	const message = error instanceof Error ? error.message : String(error);
	const lower = message.toLowerCase();

	return (
		lower.includes('fetching the script') ||
		lower.includes('failed to fetch') ||
		lower.includes('networkerror') ||
		lower.includes('network error') ||
		lower.includes('the connection was reset') ||
		lower.includes('load failed')
	);
}
